import axiosInstance from '../config/axios';

/**
 * Save exported protocol document to database
 * @param {string} projectId - Project ID
 * @param {string} project - Project ObjectId (optional)
 * @param {string} exportType - Export type ('DOCX', 'PDF', 'XML')
 * @param {string} filename - Filename
 * @param {Array} sections - Sections data
 * @param {Object} metadata - Metadata (protocolNumber, amendmentVersion, etc.)
 * @param {string} documentBase64 - Base64 encoded document (optional)
 * @param {number} fileSize - File size in bytes (optional)
 */
export const saveProtocolExport = async (projectId, project, exportType, filename, sections, metadata, documentBase64 = null, fileSize = null) => {
  try {
    console.log('💾 Saving protocol export to database...', {
      projectId,
      exportType,
      filename,
      sectionsCount: sections?.length || 0
    });

    const response = await axiosInstance.post('/export/save-protocol-export', {
      projectId,
      project,
      exportType,
      filename,
      sections,
      metadata,
      documentBase64,
      fileSize
    });

    console.log('✅ Protocol export saved:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error saving protocol export:', error);
    throw error;
  }
};

/**
 * Get protocol exports for a project
 * @param {string} projectId - Project ID
 */
export const getProtocolExports = async (projectId) => {
  try {
    const response = await axiosInstance.get(`/export/protocol-exports/${projectId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching protocol exports:', error);
    throw error;
  }
};

/**
 * Get a specific protocol export
 * @param {string} exportId - Export ID
 */
export const getProtocolExport = async (exportId) => {
  try {
    const response = await axiosInstance.get(`/export/protocol-export/${exportId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching protocol export:', error);
    throw error;
  }
};

/**
 * Email protocol PDF for a project using current sections
 * @param {string} projectId
 * @param {Array} sections
 * @param {string} toEmail (optional)
 */
export const emailProtocolPdf = async (projectId, sections, toEmail = null) => {
  try {
    const payload = {
      projectId,
      sections,
      toEmail,
    };
    const response = await axiosInstance.post('/export/email-protocol-pdf', payload);
    return response.data;
  } catch (error) {
    console.error('Error emailing protocol PDF:', error);
    throw error;
  }
};

/**
 * Save protocol PDF to blob storage and metadata to Mongo (after native PDF export)
 * @param {string} projectId - Project ID
 * @param {Blob} pdfBlob - PDF blob from @react-pdf/renderer
 * @param {Object} metadata - Optional metadata (sections count, etc.)
 */
export const saveProtocolPdfToBlob = async (projectId, pdfBlob, metadata = {}) => {
  try {
    const formData = new FormData();
    formData.append('file', pdfBlob, 'clinical-trial-protocol.pdf');
    formData.append('projectId', projectId);
    if (Object.keys(metadata).length) {
      formData.append('metadata', JSON.stringify(metadata));
    }
    const response = await axiosInstance.post('/export/save-protocol-pdf', formData, {
      headers: {
        // Let axios/browser derive the Content-Type automatically from the FormData
        // object so the multipart boundary is included. Manually setting
        // 'multipart/form-data' here strips the boundary and breaks multer.
        'Content-Type': undefined,
      },
      timeout: 60000,
    });
    return response.data;
  } catch (error) {
    console.error('Error saving protocol PDF to blob:', error);
    throw error;
  }
};


/**
 * Get protocol exports for a project
 * @param {string} projectId - Project ID
 */
/**
 * Get template prompt and structure
 * @param {string} templateId - Template/Export ID
 */
export const getTemplatePrompt = async (templateId) => {
  try {
    const response = await axiosInstance.get(`/templates/${templateId}/prompt`);
    return response.data;
  } catch (error) {
    console.error('Error fetching template prompt:', error);
    throw error;
  }
};

/**
 * Generate document from template using new data source
 * @param {string} templateId - Template/Export ID
 * @param {Object} dataSource - Data source configuration
 * @param {Object} options - Generation options
 */
export const generateFromTemplate = async (templateId, dataSource, options = {}, projectId = null) => {
  try {
    const response = await axiosInstance.post(`/templates/${templateId}/generate`, {
      dataSource,
      options,
      projectId
    });
    return response.data;
  } catch (error) {
    console.error('Error generating from template:', error);
    throw error;
  }
};

/**
 * Email protocol PDF from a file (base64 encoded PDF blob)
 * @param {string} projectId
 * @param {Blob} pdfBlob - PDF blob from native export
 * @param {string} toEmail (optional)
 */
export const emailProtocolPdfFromFile = async (projectId, pdfBlob, toEmail = null) => {
  try {
    // Convert blob to base64
    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Remove data:application/pdf;base64, prefix
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(pdfBlob);
    });

    const payload = {
      projectId,
      pdfBase64: base64,
      toEmail,
    };
    const response = await axiosInstance.post('/export/email-protocol-pdf-from-file', payload);
    return response.data;
  } catch (error) {
    console.error('Error emailing protocol PDF from file:', error);
    throw error;
  }
};

/**
 * Extract text from uploaded document for template generation
 * @param {FormData} formData - FormData containing the document file
 */
export const extractDocumentText = async (formData) => {
  try {
    const response = await axiosInstance.post('/templates/extract-document-text', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error extracting document text:', error);
    throw error;
  }
};

export default {
  saveProtocolExport,
  getProtocolExports,
  getProtocolExport,
  emailProtocolPdf,
  saveProtocolPdfToBlob,
  emailProtocolPdfFromFile,
  getTemplatePrompt,
  generateFromTemplate,
  extractDocumentText,
};













